/**
 * Visual Registry Sync — Runtime Registry ↔ Governance Map Consistency Check
 *
 * RESOLVES: CONFLICT-02 (Visual Registry vs. VISUAL_GOVERNANCE_MAP.json)
 *
 * Provides utilities to:
 * 1. Check if the runtime visual registry and governance map are in sync
 * 2. Generate a governance map from the runtime registry
 * 3. Report discrepancies for the developer diagnostics panel
 * 4. Check if the governance map covers all families in seeds (staleness check)
 *
 * This ensures that new visual types added to the registry are automatically
 * recognized by the governance pipeline without manual JSON updates.
 */

import { VISUAL_REGISTRY } from '../../visuals'
import { type VisualAidType } from '../../visuals'
import { QUESTIONS_BY_FAMILY } from '../../content'
import { debug, warn } from '../../utils/logger'
import { generateGovernanceMapFromSeeds } from './generateGovernanceMap'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VisualRegistrySyncReport {
  /** Whether registry and governance map are in sync */
  inSync: boolean

  /** Visual types in the runtime registry */
  registryTypes: string[]

  /** Visual types in the governance map */
  governanceMapTypes: string[]

  /** Types in registry but NOT in governance map (new, unreviewed) */
  missingFromGovernance: string[]

  /** Types in governance map but NOT in registry (stale, removed) */
  missingFromRegistry: string[]

  /** Types present in both (healthy) */
  synchronized: string[]

  /** Timestamp of check */
  checkedAt: string
}

// ── NEW: Governance Map Staleness Report ──────────────────────────────────────

export interface GovernanceMapStalenessReport {
  /** Whether the governance map covers all seed families */
  isFresh: boolean

  /** Total families in seeds */
  totalSeedFamilies: number

  /** Total families in governance map */
  totalMapFamilies: number

  /** Families in seeds but NOT in governance map (new, unreviewed) */
  missingFromMap: string[]

  /** Families in governance map but NOT in seeds (stale, removed) */
  missingFromSeeds: string[]

  /** Families present in both (healthy) */
  synchronized: string[]

  /** Last updated date from MAP metadata */
  mapLastUpdated: string

  /** Timestamp of check */
  checkedAt: string
}

// ─── Sync Check ───────────────────────────────────────────────────────────────

/**
 * Check synchronization between the runtime visual registry and governance map.
 *
 * @param governanceMapTypes - Array of visual type strings from governance map
 * @returns Sync report with discrepancies
 */
export function checkVisualRegistrySync(
  governanceMapTypes: string[],
): VisualRegistrySyncReport {
  const registryTypes = Object.keys(VISUAL_REGISTRY) as string[]
  const registrySet = new Set(registryTypes)
  const governanceSet = new Set(governanceMapTypes)

  const missingFromGovernance = registryTypes.filter(
    (type) => !governanceSet.has(type),
  )
  const missingFromRegistry = governanceMapTypes.filter(
    (type) => !registrySet.has(type),
  )
  const synchronized = registryTypes.filter((type) => governanceSet.has(type))

  const inSync =
    missingFromGovernance.length === 0 && missingFromRegistry.length === 0

  if (!inSync) {
    if (missingFromGovernance.length > 0) {
      warn(
        `🔴 Visual Registry Sync: ${missingFromGovernance.length} types in registry but NOT in governance map: ${missingFromGovernance.join(', ')}`,
      )
    }
    if (missingFromRegistry.length > 0) {
      warn(
        `🟡 Visual Registry Sync: ${missingFromRegistry.length} types in governance map but NOT in registry: ${missingFromRegistry.join(', ')}`,
      )
    }
  } else {
    debug(
      `✅ Visual Registry Sync: All ${synchronized.length} types are in sync`,
    )
  }

  return {
    inSync,
    registryTypes,
    governanceMapTypes,
    missingFromGovernance,
    missingFromRegistry,
    synchronized,
    checkedAt: new Date().toISOString(),
  }
}

/**
 * Generate a governance-compatible type list from the runtime registry.
 * Use this to auto-generate or update VISUAL_GOVERNANCE_MAP.json.
 */
export function generateGovernanceMapFromRegistry(): Record<
  string,
  {
    componentName: string
    isRegistered: true
  }
> {
  const map: Record<string, { componentName: string; isRegistered: true }> = {}

  for (const [type, component] of Object.entries(VISUAL_REGISTRY)) {
    map[type] = {
      componentName: component.displayName || component.name || type,
      isRegistered: true,
    }
  }

  return map
}

/**
 * Get all registered visual types from the runtime registry.
 * This is the authoritative source — governance map should match this.
 */
export function getRegisteredVisualTypes(): VisualAidType[] {
  return Object.keys(VISUAL_REGISTRY) as VisualAidType[]
}

/**
 * Check if a visual type is registered in the runtime registry.
 * Use this instead of checking VISUAL_GOVERNANCE_MAP.json for runtime validation.
 */
export function isVisualTypeRegistered(type: string): boolean {
  return type in VISUAL_REGISTRY
}

/**
 * Check if the governance map covers all families present in seeds.
 *
 * NOTE (s107): The governance map is now auto-generated from seeds via
 * generateGovernanceMapFromSeeds(). This means the map is always in sync
 * by definition. This function is kept for backward compatibility and
 * diagnostic purposes — it now compares the generated map against seeds.
 *
 * @returns Staleness report (should always be fresh with auto-generation)
 */
export function checkGovernanceMapStaleness(): GovernanceMapStalenessReport {
  // Get all family IDs from seeds
  const seedFamilyIds = Array.from(QUESTIONS_BY_FAMILY.keys()).sort()

  // Get all family IDs from auto-generated governance map
  const generatedMap = generateGovernanceMapFromSeeds()
  const mapFamilyIds = Object.keys(generatedMap.families).sort()

  const seedSet = new Set(seedFamilyIds)
  const mapSet = new Set(mapFamilyIds)

  const missingFromMap = seedFamilyIds.filter((id) => !mapSet.has(id))
  const missingFromSeeds = mapFamilyIds.filter((id) => !seedSet.has(id))
  const synchronized = seedFamilyIds.filter((id) => mapSet.has(id))

  const isFresh = missingFromMap.length === 0 && missingFromSeeds.length === 0

  if (!isFresh) {
    if (missingFromMap.length > 0) {
      warn(
        `🔴 Governance Map Staleness: ${missingFromMap.length} families in seeds but NOT in generated map: ${missingFromMap.slice(0, 5).join(', ')}${missingFromMap.length > 5 ? ` (+${missingFromMap.length - 5} more)` : ''}`,
      )
    }
    if (missingFromSeeds.length > 0) {
      warn(
        `🟡 Governance Map Staleness: ${missingFromSeeds.length} families in generated map but NOT in seeds: ${missingFromSeeds.slice(0, 5).join(', ')}${missingFromSeeds.length > 5 ? ` (+${missingFromSeeds.length - 5} more)` : ''}`,
      )
    }
  } else {
    debug(
      '✅ Governance Map is fresh — all seed families are covered (auto-generated)',
    )
  }

  return {
    isFresh,
    totalSeedFamilies: seedFamilyIds.length,
    totalMapFamilies: mapFamilyIds.length,
    missingFromMap,
    missingFromSeeds,
    synchronized,
    mapLastUpdated: 'auto-generated',
    checkedAt: new Date().toISOString(),
  }
}
